/**
 * StrategyHubPage — one home for the automation engines and the signal library,
 * split into three tabs:
 *   • "Strategies"     — the non-option in-house strategies (RockBand / Sniper / …)
 *   • "Options AT"     — the generic option auto-trade engine
 *   • "Golden Signals" — the permanent catalogue of finalised signals
 *
 * The pages keep their existing layouts; this just nests them under one header
 * + tab strip (each page is rendered with `embedded` so it drops its own page
 * title/padding). Tab is remembered across visits; a /option-strategies deep
 * link opens the second and /golden-signals opens the third (and so do the
 * matching command-palette actions).
 *
 * THE THIRD TAB IS A CATALOGUE, NOT AN ENGINE. The first two hold cards that
 * read ACTIVE / PAUSED and really place orders; Golden Signals only describes
 * signals and has no action to offer. That is the whole distinction, so the tab
 * label and the page's own lede both say so rather than relying on the reader
 * to notice.
 */
import React, { useEffect } from 'react';
import { Box, Tabs, Tab, Typography, useMediaQuery, useTheme } from '@mui/material';
import ShowChartIcon from '@mui/icons-material/ShowChart';
import TuneIcon from '@mui/icons-material/Tune';
import BookmarksIcon from '@mui/icons-material/Bookmarks';
import { useLocation } from 'react-router-dom';
import { usePersistedState } from '../hooks/usePersistedState';
import StrategiesPage from './StrategiesPage';
import OptionStrategiesPage from './OptionStrategiesPage';
import GoldenSignalsPage from './GoldenSignalsPage';

const StrategyHubPage: React.FC = () => {
  const location = useLocation();
  const theme = useTheme();
  /* Three labels do not fit at phone width. Scrollable tabs stopped the third
     one being clipped to "Go", but MUI could still only bring "Gold" into view
     - so the name itself shortens where there is no room for it. The full name
     is what the guide and the page lede use; this is the tab strip only. */
  const narrow = useMediaQuery(theme.breakpoints.down('sm'));
  const [tab, setTab] = usePersistedState<number>('strategyHub.tab', 0);

  // Deep links pick the tab. /option-strategies opens tab 2, /golden-signals
  // tab 3 — the same mechanism, so the guide and the command palette can point
  // at a tab without the hub needing a query-param convention of its own.
  useEffect(() => {
    if (location.pathname === '/option-strategies') setTab(1);
    else if (location.pathname === '/golden-signals') setTab(2);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  return (
    <Box sx={{ p: 2, width: '100%' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', mb: 1 }}>
        <Typography variant="h5" sx={{ fontWeight: 800 }}>Strategies</Typography>
      </Box>
      {/* Scrollable: at 380px the three labels do not fit, and the third one
          rendered as "Go". Fixed tabs clip rather than wrap. */}
      <Tabs
        value={tab}
        onChange={(_, v) => setTab(v)}
        variant="scrollable"
        scrollButtons="auto"
        allowScrollButtonsMobile
        sx={{ mb: 1, minHeight: 40, '& .MuiTab-root': { minHeight: 40, textTransform: 'none', fontWeight: 700 } }}
      >
        <Tab icon={<ShowChartIcon fontSize="small" />} iconPosition="start" label="Strategies" />
        <Tab icon={<TuneIcon fontSize="small" />} iconPosition="start" label="Options AT" />
        <Tab
          icon={<BookmarksIcon fontSize="small" />} iconPosition="start"
          label={narrow ? 'Signals' : 'Golden Signals'}
        />
      </Tabs>

      {/* Mount only the active tab so the inactive page's polling stops. */}
      {tab === 0 ? <StrategiesPage embedded />
        : tab === 1 ? <OptionStrategiesPage embedded />
          : <GoldenSignalsPage embedded />}
    </Box>
  );
};

export default StrategyHubPage;
